import { useState } from 'react';
import { Apple, Loader2, AlertCircle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { createCheckoutSession } from '@/lib/api';
import { CONTACT_INFO } from '@/lib/data';
import { LUXE } from '@/constants/testIds';

/**
 * Mandatory 50% deposit step. Booking is only officially submitted after
 * Stripe payment completes. Zelle/other methods must be arranged by phone.
 */
export const PaymentStep = ({ booking, pricing }) => {
  const [loading, setLoading] = useState(false);

  const deposit = pricing?.deposit ?? booking.deposit_amount ?? 0;
  const subtotal = pricing?.subtotal ?? booking.subtotal ?? 0;

  const handleStripe = async () => {
    setLoading(true);
    try {
      const { url } = await createCheckoutSession({
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      window.location.href = url;
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not start checkout. Try again.');
      setLoading(false);
    }
  };

  return (
    <div
      data-testid={LUXE.paymentDepositCard}
      className="luxe-card p-8 sm:p-10 border-[#C9A24A]/60"
    >
      <div className="luxe-eyebrow mb-3 text-[#C9A24A] flex items-center gap-2">
        <AlertCircle size={12} /> Required · Secure Your Slot
      </div>
      <h2 className="text-3xl text-white font-serif mb-3">
        Place your <span className="italic text-[#C9A24A]">50% deposit</span> to submit your booking.
      </h2>
      <p className="text-white/60 leading-relaxed">
        A deposit locks in your appointment. Your booking is not officially
        submitted until the deposit is completed. Balance is paid at the
        appointment.
      </p>

      <div className="mt-6 p-5 bg-[#0B0F1A] border border-[#C9A24A]/30">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <div className="luxe-eyebrow mb-1">Estimated Total</div>
            <div className="text-2xl font-serif text-white">${subtotal.toFixed(2)}</div>
          </div>
          <div className="text-right">
            <div className="luxe-eyebrow mb-1">50% Deposit</div>
            <div
              data-testid={LUXE.paymentDepositAmount}
              className="text-4xl font-serif text-[#C9A24A]"
            >
              ${deposit.toFixed(2)}
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        data-testid={LUXE.paymentPayApplePay}
        onClick={handleStripe}
        disabled={loading}
        className="btn-gold w-full mt-6 !py-4 disabled:opacity-60"
      >
        {loading ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Redirecting to secure checkout...
          </>
        ) : (
          <>
            <Apple size={16} /> Pay Deposit — Apple Pay / Card
          </>
        )}
      </button>

      <div className="mt-4 border border-[#C9A24A]/30 bg-[#0B0F1A] p-4 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="luxe-eyebrow mb-1">Prefer Cash App?</div>
          <p className="text-white/50 text-xs leading-relaxed">
            Send your ${deposit.toFixed(2)} deposit to{' '}
            <span className="text-white/80">{CONTACT_INFO.cashapp}</span> — include your
            name + service in the note, then text us so we can confirm manually.
          </p>
        </div>
        <a
          href={CONTACT_INFO.cashappUrl}
          target="_blank"
          rel="noreferrer"
          data-testid="payment-cashapp-link"
          className="shrink-0 border border-[#C9A24A] text-[#C9A24A] px-5 py-2.5 text-xs tracking-[0.2em] uppercase hover:bg-[#C9A24A] hover:text-[#0B0F1A] transition-colors"
        >
          Open Cash App
        </a>
      </div>

      <div className="mt-5 flex items-start gap-3 p-4 bg-[#0B0F1A]/60 border border-white/5">
        <ShieldCheck size={14} className="text-[#C9A24A] mt-0.5 shrink-0" />
        <p className="text-white/50 text-xs leading-relaxed">
          Payments are processed securely by{' '}
          <span className="text-white/80">Stripe</span>. Apple Pay, Google Pay,
          and all major cards accepted. Prefer Zelle or another method? Call us
          at{' '}
          <a
            href={`tel:${CONTACT_INFO.phoneRaw}`}
            className="text-[#C9A24A] hover:underline"
            data-testid="payment-alt-call"
          >
            {CONTACT_INFO.phone}
          </a>{' '}
          to arrange manually before booking.
        </p>
      </div>
    </div>
  );
};
